import React, { useRef, useState, useMemo } from 'react';
import heic2any from 'heic2any';
import { useFormik } from 'formik';
import { bool, func, shape, string } from 'prop-types';
import { NotificationManager } from 'react-notifications';
import { createValidationSchema, normalizeGender } from './helpers';
import { Button, Col, Row } from "react-bootstrap";
import { deleteUserAccount, updateUserPhoto } from "../../services/users";
import { languages, normalizeLanguage } from "./../../utils/index";
import { useTranslation } from "react-i18next";

// React Components
import CheckBox from 'components/CheckBox';
import ErrorMessage from 'components/ErrorMessage';
import Input from 'components/Input/Input';
import InputRadio from 'components/InputRadio';
import InputSelect from 'components/InputSelect';
import BulletHeader from "../../components/BulletHeader/BulletHeader";
import MySwal from "sweetalert2";
import ImageCropper from "../../components/ImageCropper/ImageCropper";
import { useAuth } from '../../contexts/authContext';

//Icons
import CameraOverlay from "../../assets/images/cameraoverlay.svg";

// CSS Files
import './styles.css';

const Form = props => {
  const { t } = useTranslation();
  const [rawImage, setRawImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const { setUser, user } = useAuth();
  const validationSchema = useMemo(() => createValidationSchema(t), [t]);

  const {
    onSubmit,
    user: {
      name = '',
      birthyear,
      gender,
      phone_number,
      fpp_licence,
      site_url,
      social_media,
      email,
      photo_url,
      wants_app_notifications,
      daily_match_notification,
      match_day_notification,
      matches_to_be_played_notification,
      language,
    },
    submitting
  } = props;

  const initialValues = {
    birthyear: birthyear == null ? '' : String(birthyear),
    email,
    facebook: site_url,
    fppLicence: fpp_licence == null ? '' : String(fpp_licence),
    gender: normalizeGender(gender),
    hasFppLicence: !!fpp_licence,
    hasSocialMedia: !!social_media,
    name,
    phone: phone_number == null ? '' : String(phone_number),
    photo_url: photo_url,
    password: '',
    passwordConfirmation: '',
    wantsAppNotifications: !!wants_app_notifications,
    divisionMatches: !!daily_match_notification,
    sameDaySameClubMatches: !!match_day_notification,
    unscheduledLeagueMatches: !!matches_to_be_played_notification,
    language: normalizeLanguage(language)
  };

  const formikProps = useFormik({ initialValues, onSubmit, validationSchema });

  const imgUploader = useRef(null);

  const isNameDisabled = false;

  const cacheBustedPhotoUrl = useMemo(() => {
    const baseUrl = formikProps.values.photo_url;
    if (!baseUrl || baseUrl === "") return null;
    return `${baseUrl}?v=${Date.now()}`;
  }, [formikProps.values.photo_url]);

  function submit(event) {
    event.preventDefault();
    formikProps.handleSubmit();
  }

  return (
    <form className="form-container" onSubmit={submit}>
      <Row>
        <Col xs={{ span: 4, offset: 4 }} md={{ span: 2, offset: 5 }}>
          <div className={"profilefoto iconchager position-relative editable"} onKeyDown={() => imgUploader.current.click()} onClick={() => imgUploader.current.click()}
               style={{ backgroundImage: `url(${cacheBustedPhotoUrl || process.env.PUBLIC_URL + "/img/faceless-photo.svg"})` }}>
            <img src={CameraOverlay} className={"camaraoverlay position-absolute fixed-bottom"} alt={"change profile picture logo"} />
          </div>

        </Col>
      </Row>
      <Row>
        <Col xs={12} className={"text-center pt-3 pb-3"}><b>{formikProps.values.email}</b><br /><span className={"text-muted"}>{t("user_profile_page.change_email_notice")}</span></Col>
      </Row>
      <Row className={"d-none"}>
        <input
          name="photo_url"
          type="file"
          id="file"
          ref={imgUploader}
          style={{ display: "none" }}
          onChange={async (event) => {
            const file = event.currentTarget.files[0];
            if (!file) return;

            const isHeic = file.type === 'image/heic' || file.type === 'image/heif' ||
              file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');

            try {
              const sourceFile = isHeic
                ? await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 })
                : file;

              const reader = new FileReader();
              reader.onload = () => {
                setRawImage(reader.result);
                setShowCropper(true);
              };
              reader.readAsDataURL(sourceFile instanceof Blob ? sourceFile : sourceFile[0]);
            } catch (err) {
              console.error('Image load error:', err);
              alert(t('user_profile_page.errors.process_image'));
            }
          }}
        />

        <Input
          name="email"
          text={t("user_profile_page.labels.email")}
          inputType="text"
          disabled
          inputTextValue={formikProps.values.email}
        />
      </Row>
      <Row>
        <Col xs={12} sm={6}>
          <BulletHeader text={t("user_profile_page.personal_data")} />
          <ErrorMessage
            errors={formikProps.errors}
            touched={formikProps.touched}
            name="email"
          />

          <Input
            name="name"
            text={t("common.form.labels.name")}
            inputType="text"
            disabled={isNameDisabled}
            handleChange={formikProps.handleChange}
            handleOnBlur={formikProps.handleBlur}
            inputTextValue={formikProps.values.name}
            hasError={!!formikProps.errors.name}
          />

          <ErrorMessage
            errors={formikProps.errors}
            touched={formikProps.touched}
            name="name"
          />

          <InputSelect labelClasses="label-style" inputType="text" text={t("common.form.labels.gender")}>
            <select
              onChange={formikProps.handleChange}
              value={
                formikProps.values.gender !== ''
                  ? formikProps.values.gender
                  : 'DEFAULT'
              }
              name="gender"
              className="input-background"
            >
              <option value="DEFAULT" disabled>
                {t("common.form.options.choose_an_option")}
              </option>
              <option value="Masculino">{t("common.form.options.male")}</option>
              <option value="Feminino">{t("common.form.options.female")}</option>
            </select>
          </InputSelect>

          <ErrorMessage
            errors={formikProps.errors}
            touched={formikProps.touched}
            name="gender"
          />

          <Input
            name="birthyear"
            text={t("common.form.labels.birth_year")}
            inputType="number"
            handleChange={formikProps.handleChange}
            handleOnBlur={formikProps.handleBlur}
            inputTextValue={formikProps.values.birthyear}
            hasError={!!formikProps.errors.birthyear}
          />

          <ErrorMessage
            errors={formikProps.errors}
            touched={formikProps.touched}
            name="birthyear"
          />

          <Input
            name="phone"
            text={t("common.form.labels.phone_number")}
            inputType="tel"
            handleChange={formikProps.handleChange}
            handleOnBlur={formikProps.handleBlur}
            inputTextValue={formikProps.values.phone}
            hasError={!!formikProps.errors.phone}
          />

          <ErrorMessage
            errors={formikProps.errors}
            touched={formikProps.touched}
            name="phone"
          />

          <Input
            name="password"
            text={t("common.form.labels.change_password")}
            inputType="password"
            handleChange={formikProps.handleChange}
            handleOnBlur={formikProps.handleBlur}
            hasError={!!formikProps.errors.password}
            inputTextValue={formikProps.values.password}
          />

          <ErrorMessage
            errors={formikProps.errors}
            touched={formikProps.touched}
            name="password"
          />

          <Input
            name="passwordConfirmation"
            text={t("common.form.labels.confirm_password")}
            inputType="password"
            onPaste={e => e.preventDefault()}
            handleChange={formikProps.handleChange}
            handleOnBlur={formikProps.handleBlur}
            hasError={!!formikProps.errors.passwordConfirmation}
            inputTextValue={formikProps.values.passwordConfirmation}
          />

          < ErrorMessage
            errors={formikProps.errors}
            touched={formikProps.touched}
            name="passwordConfirmation"
          />

          <InputRadio
            handleChange={formikProps.handleChange}
            name="hasFppLicence"
            text={t("common.form.labels.is_federated")}
            firstRadioText={t("common.yes")}
            secondRadioText={t("common.no")}
            checked={formikProps.values.hasFppLicence}
          />

          {formikProps.values.hasFppLicence && (
            <Input
              name="fppLicence"
              text={t("common.form.labels.federative_license")}
              inputType="number"
              handleChange={formikProps.handleChange}
              handleOnBlur={formikProps.handleBlur}
              inputTextValue={formikProps.values.fppLicence}
              hasError={!!formikProps.errors.fppLicence}
            />
          )}

          {formikProps.values.hasFppLicence && (
            <ErrorMessage
              errors={formikProps.errors}
              touched={formikProps.touched}
              name="fppLicence"
            />
          )}
        </Col>
        <Col xs={12} sm={6}>
          <BulletHeader text={t("common.form.labels.social_networks")} />
          <InputRadio
            handleChange={formikProps.handleChange}
            name="hasSocialMedia"
            isColumn
            text={t("common.form.labels.wants_identification")}
            firstRadioText={t("common.yes")}
            secondRadioText={t("common.no")}
            checked={formikProps.values.hasSocialMedia}
          />

          {formikProps.values.hasSocialMedia && (
            <Input
              name="facebook"
              text="facebook.com/"
              inputType="text"
              layout="row"
              handleChange={formikProps.handleChange}
              handleOnBlur={formikProps.handleBlur}
              inputTextValue={formikProps.values.facebook}
              hasError={!!formikProps.errors.facebook}
            />

          )}

          {formikProps.values.hasSocialMedia && (
            <ErrorMessage
              errors={formikProps.errors}
              touched={formikProps.touched}
              name="facebook"
            />
          )}

          <BulletHeader
            text={t("common.form.labels.notifications")}
            showInfoButton
            infoButtonOnClick={() => {
              MySwal.fire({
                titleText: t("user_profile_page.notifications_popup.header"),
                text: t("user_profile_page.notifications_popup.content"),
                confirmButtonText: "Ok",
                allowOutsideClick: false,
                allowEscapeKey: false,
              }).then(async (res) => {
                MySwal.close();
              });
            }}
          />

          <InputRadio
            handleChange={formikProps.handleChange}
            name="wantsAppNotifications"
            isColumn
            text={t("user_profile_page.notification_type")}
            firstRadioText="App"
            secondRadioText="E-mail"
            checked={formikProps.values.wantsAppNotifications}
          />

          <label className={"form-label"}>{t("user_profile_page.optional_notifications")}</label>

          <CheckBox
            handleChange={formikProps.handleChange}
            id="divisionMatches"
            checked={formikProps.values.divisionMatches}
            name="divisionMatches"
            text={t("user_profile_page.daily_matches_notification")}
          />

          <CheckBox
            handleChange={formikProps.handleChange}
            id="sameDaySameClubMatches"
            checked={formikProps.values.sameDaySameClubMatches}
            name="sameDaySameClubMatches"
            text={t("user_profile_page.daily_matches_club_notification")}
          />

          <CheckBox
            handleChange={formikProps.handleChange}
            id="unscheduledLeagueMatches"
            checked={formikProps.values.unscheduledLeagueMatches}
            name="unscheduledLeagueMatches"
            text={t("user_profile_page.remaining_matches_notification")}
          />

          <BulletHeader text={t("common.form.labels.language")}/>

          <InputSelect inputType="text">
            <select
               onChange={formikProps.handleChange}
               value={formikProps.values.language !== '' ? normalizeLanguage(formikProps.values.language) : 'DEFAULT'}
               name="language"
               className="input-background"
            >
               <option value="DEFAULT" disabled>{t("common.form.options.choose_an_option")}</option>
               {Object.keys(languages).map((language) => {
                  return <option key={language} value={language}>{languages[language].nativeName}</option>
               })}
            </select>
          </InputSelect>

          <p className={"mt-3 delete-account"} onClick={() =>
            MySwal.fire({
              titleText: t("user_profile_page.delete_account"),
              text: t("user_profile_page.delete_account_notice"),
              input: "password",
              inputAttributes: {
                autocapitalize: "off"
              },
              showCancelButton: true,
              cancelButtonText: t("common.cancel"),
              confirmButtonText: t("common.yes"),
              allowOutsideClick: false,
              allowEscapeKey: false,
              preConfirm: async (password) => {
                if (password === "") {
                  NotificationManager.error(t("common.form.errors.mandatory_field"), t("common.error"), 4000);
                  return false;
                }

                try {
                  return await deleteUserAccount(password);
                } catch (e) {
                  if (e.status === 400 || e.status === 401) {
                    NotificationManager.error(e.data, t("common.error"), 4000);
                    return false;
                  } else {
                    NotificationManager.error(t("common.form.errors.general"), t("common.error"), 4000);
                    return false;
                  }
                }
              }
            }).then(async (result) => {
              if (result.dismiss === MySwal.DismissReason.cancel) {
                MySwal.close();
              } else {
                MySwal.fire({
                  titleText: t("user_profile_page.delete_account"),
                  text: `${result.value}`,
                  confirmButtonText: "Ok",
                  allowOutsideClick: false,
                  allowEscapeKey: false,
                }).then(async (res) => {
                  window.location.reload();
                });
              }
            })
          }>{t("user_profile_page.delete_account")}</p>
        </Col>
      </Row>
      <Row className={"pb-5"}>
        <Col xs={12} sm={{ span: 6, offset: 3 }} className={"pt-3"}>
          <Button
            type="submit"
            className="button button-background-primary button-text-style-primary submit-btn"
          >
            {submitting ? t("user_profile_page.updating") : t("user_profile_page.update")}
          </Button>
        </Col>
      </Row>
      {showCropper && (
        <ImageCropper
          image={rawImage}
          aspect={1}
          onUpload={updateUserPhoto}
          onUploadSuccess={(photoPath) => {
            NotificationManager.success(t("user_profile_page.photo_uploaded_success"), t("common.success"), 5000);
            formikProps.setFieldValue("photo_url", photoPath);
            setUser({ ...user, photo_url: photoPath });
            setShowCropper(false);
            setRawImage(null);
          }}
          onCancel={() => {
            setShowCropper(false);
            setRawImage(null);
            if (imgUploader.current) imgUploader.current.value = null;
          }}
        />
      )}
    </form>
  );
};

Form.propTypes = {
  onSubmit: func.isRequired,
  user: shape({
    name: string,
    phone_number: string,
    fpp_licence: string,
    facebook: string,
    email: string,
    photo_url: string
  }).isRequired,
  submitting: bool
};

Form.defaultProps = {
  submitting: false
};

export default Form;
